import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Sparkles, Target, Clock, ArrowRight, Check, Plus, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_COLORS = {
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-blue-100 text-blue-700",
};

export default function AISuggestGoalsDialog({ open, onOpenChange, onGoalCreated }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [creating, setCreating] = useState({});
  const [created, setCreated] = useState({});
  const [error, setError] = useState(null);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);
    setSuggestions([]);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/goals/ai-suggest`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuggestions(res.data.suggestions || []);
      if (!res.data.suggestions?.length) {
        setError("AI couldn't generate suggestions. Try completing an assessment first.");
      }
    } catch (err) {
      if (err._handled401) return;
      setError("Failed to generate suggestions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const acceptGoal = async (suggestion, index) => {
    setCreating((prev) => ({ ...prev, [index]: true }));
    try {
      const token = localStorage.getItem("token");
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + (suggestion.timeline_weeks || 8) * 7);

      await axios.post(`${API}/goals`, {
        title: suggestion.title,
        description: suggestion.description,
        category: suggestion.category || "personal-enhancement",
        target_date: targetDate.toISOString(),
        milestones: [],
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCreated((prev) => ({ ...prev, [index]: true }));
      toast.success(`Goal "${suggestion.title}" created!`);
      if (onGoalCreated) onGoalCreated();
    } catch (err) {
      if (err._handled401) return;
      toast.error("Failed to create goal");
    } finally {
      setCreating((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleOpen = (val) => {
    onOpenChange(val);
  };

  // Trigger fetch when dialog opens
  useEffect(() => {
    if (open && suggestions.length === 0 && !loading) {
      fetchSuggestions();
    }
  }, [open]);

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ai-suggest-goals-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-800">
            <Sparkles className="w-5 h-5" /> AI Goal Suggestions
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Personalized goals based on your assessment and wellness data
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center" data-testid="ai-goals-loading">
            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">Analyzing your profile...</p>
            <p className="text-[10px] text-gray-400 mt-1">This may take a few seconds</p>
          </div>
        )}

        {error && !loading && (
          <div className="py-8 text-center" data-testid="ai-goals-error">
            <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">{error}</p>
            <Button onClick={fetchSuggestions} variant="outline" size="sm" className="mt-3 text-xs">
              Try Again
            </Button>
          </div>
        )}

        {!loading && suggestions.length > 0 && (
          <div className="space-y-3" data-testid="ai-goals-list">
            {suggestions.map((s, i) => (
              <div
                key={i}
                data-testid={`ai-goal-suggestion-${i}`}
                className={`rounded-xl border p-4 transition-all ${
                  created[i]
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-white border-gray-200 hover:border-emerald-300 hover:shadow-sm"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h4 className="text-sm font-bold text-gray-900">{s.title}</h4>
                      {s.priority && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${PRIORITY_COLORS[s.priority] || PRIORITY_COLORS.medium}`}>
                          {s.priority}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">{s.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
                      {s.focus_area && (
                        <span className="flex items-center gap-1">
                          <Target className="w-3 h-3" /> {s.focus_area}
                        </span>
                      )}
                      {s.timeline_weeks && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {s.timeline_weeks} weeks
                        </span>
                      )}
                      {s.category && (
                        <span className="capitalize">{s.category.replace("-", " ")}</span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0">
                    {created[i] ? (
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Check className="w-4 h-4 text-emerald-600" />
                      </div>
                    ) : (
                      <Button
                        data-testid={`accept-goal-${i}`}
                        size="sm"
                        onClick={() => acceptGoal(s, i)}
                        disabled={creating[i]}
                        className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-xs font-semibold"
                      >
                        {creating[i] ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div className="pt-2 flex justify-between items-center">
              <Button onClick={fetchSuggestions} variant="outline" size="sm" className="text-xs" disabled={loading}>
                <Sparkles className="w-3.5 h-3.5 mr-1" /> Regenerate
              </Button>
              <Button onClick={handleClose} size="sm" className="text-xs bg-gray-800 hover:bg-gray-900">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
